import type { Dispatch } from 'react';

import type { ConfirmFn } from '../seams';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';

// The two confirm dialogs of the timeline logger's focus card, extracted from
// useTimelineLogger (AP-0b 500-line split) — behaviour unchanged.
//
// Sciogli — dissolve the active group. Shared by the FocusPanel ⋯ menu and
// the SupersetTabs ⊗ control so there's one confirm dialog (the v2.67 smoke
// "no way out of superset" → a VISIBLE exit on the notebook header).
//
// Header ✕ — remove the active exercise from the workout. Confirmed (an entry
// with logged sets loses them), mirroring the card logger's EntryHeader so the
// two loggers behave identically; reuses workout.removeExercise* i18n. After
// removal the caller's onRemoved blanks the focus (Option B point c: no
// fallback — re-expand the roster so the athlete re-picks).
//
// ⛔ Both go through the confirm SEAM, not react-native's Alert (RW-25):
// inside packages/ui an Alert is a no-op on react-native-web, so on the web
// the ⊗ that dissolves a superset and the ✕ that removes the focused exercise
// were both dead controls — they asked a question nobody ever saw.
export function useEntryDialogs(args: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  dispatch: Dispatch<LoggerAction>;
  confirm: ConfirmFn;
  getExerciseNameBySlug: (slug: string, fallback: string) => string;
  activeEntry: DraftEntry | null;
  activeEntryIdx: number;
  /** Resets the focus state after a remove (activeId null + roster open). */
  onRemoved: () => void;
}) {
  const {
    t,
    dispatch,
    confirm,
    getExerciseNameBySlug,
    activeEntry,
    activeEntryIdx,
    onRemoved,
  } = args;

  const confirmSplitGroup = (groupId: string) => {
    void confirm({
      title: t('workout.splitGroupTitle', { defaultValue: 'Split this group?' }),
      body: t('workout.splitGroupBody', {
        defaultValue:
          'Members keep their sets and stay in the workout, just unlinked. You can re-group them anytime.',
      }),
      confirmLabel: t('workout.splitGroupConfirm', { defaultValue: 'Split' }),
      cancelLabel: t('workout.cancel'),
    }).then((ok) => {
      if (ok) dispatch({ type: 'split_group', groupId });
    });
  };

  const onRemoveExercise = () => {
    if (!activeEntry || activeEntryIdx < 0) return;
    void confirm({
      title: t('workout.removeExercise'),
      body: t('workout.removeExerciseConfirm', {
        name: getExerciseNameBySlug(
          activeEntry.exerciseId,
          activeEntry.exerciseName,
        ),
      }),
      confirmLabel: t('workout.removeExercise'),
      cancelLabel: t('workout.cancel'),
    }).then((ok) => {
      if (!ok) return;
      dispatch({ type: 'remove_exercise', entryIdx: activeEntryIdx });
      onRemoved();
    });
  };

  return { confirmSplitGroup, onRemoveExercise };
}
